"use client"

import { useCallback, useRef, useState, useEffect } from "react"
import { cn } from "@/lib/utils"
import type { Position, Size } from "./types"

interface DraggableItemProps {
  id: string
  position: Position
  size: Size
  rotation?: number
  selected?: boolean
  disabled?: boolean
  minWidth?: number
  minHeight?: number
  aspectRatio?: number
  containerRef: React.RefObject<HTMLElement | null>
  onPositionChange: (id: string, position: Position) => void
  onSizeChange: (id: string, size: Size) => void
  onSelect: (id: string) => void
  onDoubleClick?: (id: string) => void
  children: React.ReactNode
}

type ResizeHandle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w"

export function DraggableItem({
  id,
  position,
  size,
  rotation = 0,
  selected = false,
  disabled = false,
  minWidth = 30,
  minHeight = 30,
  aspectRatio,
  containerRef: _containerRef,
  onPositionChange,
  onSizeChange,
  onSelect,
  onDoubleClick,
  children,
}: DraggableItemProps) {
  const itemRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [activeHandle, setActiveHandle] = useState<ResizeHandle | null>(null)
  const dragStart = useRef<{ x: number; y: number; posX: number; posY: number } | null>(null)
  const resizeStart = useRef<{
    x: number
    y: number
    width: number
    height: number
    posX: number
    posY: number
    handle: ResizeHandle
  } | null>(null)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return
      if ((e.target as HTMLElement).dataset.handle) return

      e.preventDefault()
      e.stopPropagation()
      onSelect(id)
      setIsDragging(true)
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        posX: position.x,
        posY: position.y,
      }
    },
    [disabled, id, onSelect, position]
  )

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onDoubleClick?.(id)
    },
    [id, onDoubleClick]
  )

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, handle: ResizeHandle) => {
      if (disabled) return
      e.preventDefault()
      e.stopPropagation()
      setIsResizing(true)
      setActiveHandle(handle)
      resizeStart.current = {
        x: e.clientX,
        y: e.clientY,
        width: size.width,
        height: size.height,
        posX: position.x,
        posY: position.y,
        handle,
      }
    },
    [disabled, size, position]
  )

  useEffect(() => {
    if (!isDragging && !isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && dragStart.current) {
        const deltaX = e.clientX - dragStart.current.x
        const deltaY = e.clientY - dragStart.current.y
        onPositionChange(id, {
          x: dragStart.current.posX + deltaX,
          y: dragStart.current.posY + deltaY,
        })
      }

      if (isResizing && resizeStart.current) {
        const { x, y, width, height, posX, posY, handle } = resizeStart.current
        const deltaX = e.clientX - x
        const deltaY = e.clientY - y

        let newWidth = width
        let newHeight = height
        let newX = posX
        let newY = posY

        if (handle.includes("e")) newWidth = Math.max(minWidth, width + deltaX)
        if (handle.includes("w")) {
          newWidth = Math.max(minWidth, width - deltaX)
          newX = posX + (width - newWidth)
        }
        if (handle.includes("s")) newHeight = Math.max(minHeight, height + deltaY)
        if (handle.includes("n")) {
          newHeight = Math.max(minHeight, height - deltaY)
          newY = posY + (height - newHeight)
        }

        if (aspectRatio && e.shiftKey === false) {
          if (handle === "e" || handle === "w") {
            newHeight = newWidth / aspectRatio
          } else if (handle === "n" || handle === "s") {
            newWidth = newHeight * aspectRatio
          } else {
            const ratio = Math.max(newWidth / width, newHeight / height)
            newWidth = width * ratio
            newHeight = height * ratio
            if (handle.includes("w")) newX = posX + width - newWidth
            if (handle.includes("n")) newY = posY + height - newHeight
          }
        }

        onSizeChange(id, { width: newWidth, height: newHeight })
        onPositionChange(id, { x: newX, y: newY })
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      setIsResizing(false)
      setActiveHandle(null)
      dragStart.current = null
      resizeStart.current = null
    }

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isDragging, isResizing, id, onPositionChange, onSizeChange, minWidth, minHeight, aspectRatio])

  const handles: ResizeHandle[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"]

  const getHandleStyle = (handle: ResizeHandle) => {
    const base = "absolute bg-white border-2 border-primary shadow-sm z-20 transition-transform"
    const size = "w-3 h-3 rounded-sm"
    const activeScale = activeHandle === handle ? "scale-125" : "hover:scale-110"

    const positions: Record<ResizeHandle, string> = {
      nw: "-top-1.5 -left-1.5 cursor-nw-resize",
      n: "-top-1.5 left-1/2 -translate-x-1/2 cursor-n-resize",
      ne: "-top-1.5 -right-1.5 cursor-ne-resize",
      e: "top-1/2 -right-1.5 -translate-y-1/2 cursor-e-resize",
      se: "-bottom-1.5 -right-1.5 cursor-se-resize",
      s: "-bottom-1.5 left-1/2 -translate-x-1/2 cursor-s-resize",
      sw: "-bottom-1.5 -left-1.5 cursor-sw-resize",
      w: "top-1/2 -left-1.5 -translate-y-1/2 cursor-w-resize",
    }

    return cn(base, size, activeScale, positions[handle])
  }

  return (
    <div
      ref={itemRef}
      className={cn(
        "absolute select-none transition-shadow",
        selected && "z-10",
        (isDragging || isResizing) && "z-20",
        !disabled && !isDragging && !isResizing && "cursor-move"
      )}
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      onClick={(e) => {
        e.stopPropagation()
        onSelect(id)
      }}
    >
      <div
        className={cn(
          "w-full h-full overflow-hidden rounded-sm transition-all",
          selected && "ring-2 ring-primary ring-offset-2 ring-offset-white",
          isDragging && "opacity-90 shadow-2xl",
          !selected && !disabled && "hover:ring-2 hover:ring-primary/30"
        )}
      >
        {children}
      </div>

      {selected && !disabled && (
        <>
          {handles.map((handle) => (
            <div
              key={handle}
              data-handle={handle}
              className={getHandleStyle(handle)}
              onMouseDown={(e) => handleResizeStart(e, handle)}
            />
          ))}

          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded whitespace-nowrap pointer-events-none">
            {Math.round(size.width)} × {Math.round(size.height)}
          </div>
        </>
      )}
    </div>
  )
}
